import { useState, useMemo } from 'react';

const ROLE_TILES = [
  { key: "admin", label: "Admin", color: "text-[#0f2d5c]", bgColor: "bg-[#0f2d5c]/10", iconColor: "text-[#0f2d5c]",
    icon: <path d="M12 15l-2-2m0 0l2-2m-2 2h12m-6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" /> },
  { key: "manager", label: "Managers", color: "text-blue-600", bgColor: "bg-blue-50", iconColor: "text-blue-600",
    icon: <><path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4-4v2" /><circle cx="8.5" cy="7" r="4" /></> },
  { key: "contractor", label: "Contractors", color: "text-amber-600", bgColor: "bg-amber-50", iconColor: "text-amber-600",
    icon: <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" /> },
  { key: "stakeholder", label: "Stakeholders", color: "text-slate-600", bgColor: "bg-slate-100", iconColor: "text-slate-500",
    icon: <><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4-4v2" /><circle cx="8.5" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87" /><path d="M16 3.13a4 4 0 010 7.75" /></> },
];

const ROLE_BADGE_COLORS = {
  admin: "bg-[#0f2d5c]/10 text-[#0f2d5c]",
  manager: "bg-blue-100 text-blue-700",
  contractor: "bg-amber-100 text-amber-700",
  stakeholder: "bg-slate-200 text-slate-600",
};

const AVATAR_COLORS = ["bg-[#0f2d5c]", "bg-blue-600", "bg-amber-500", "bg-emerald-600", "bg-purple-600", "bg-rose-500"];

function getInitials(name) {
  if (!name) return "?";
  const parts = name.split(" ").filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

function normalizeRole(type) {
  const t = (type || "").toLowerCase();
  if (t.includes("admin")) return "admin";
  if (t.includes("manager")) return "manager";
  if (t.includes("contractor") || t.includes("vendor")) return "contractor";
  return "stakeholder";
}

export default function DashboardTeam({ projects, contacts, onNavigate }) {
  const [projectFilter, setProjectFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState(null);

  const roleCounts = useMemo(() => {
    const counts = { admin: 0, manager: 0, contractor: 0, stakeholder: 0 };
    for (const c of (contacts || [])) {
      counts[normalizeRole(c.type)]++;
    }
    return counts;
  }, [contacts]);

  const teamWorkload = useMemo(() => {
    const relevantProjects = projectFilter === "all"
      ? projects
      : projects.filter(p => p.id === projectFilter);

    const byPerson = {};
    for (const p of relevantProjects) {
      for (const t of (p.tasks || [])) {
        if (t.isPhaseHeader || !t.assignedTo) continue;
        const name = t.assignedTo.trim();
        if (!byPerson[name]) {
          byPerson[name] = { name, tasks: 0, statuses: {}, projectSet: new Set() };
        }
        byPerson[name].tasks++;
        byPerson[name].statuses[t.status] = (byPerson[name].statuses[t.status] || 0) + 1;
        byPerson[name].projectSet.add(p.name);
      }
    }

    const workload = Object.values(byPerson).map(w => {
      const contact = (contacts || []).find(c => c.name === w.name);
      return {
        ...w,
        role: contact ? normalizeRole(contact.type) : "contractor",
        company: contact?.company || "",
        projectCount: w.projectSet.size,
      };
    });
    workload.sort((a, b) => b.tasks - a.tasks);
    return workload;
  }, [projects, contacts, projectFilter]);

  const unassignedByProject = useMemo(() => {
    const result = [];
    for (const p of projects) {
      const unassigned = (p.tasks || []).filter(t => !t.isPhaseHeader && !t.assignedTo && t.status !== "Complete").length;
      if (unassigned > 0) result.push({ id: p.id, name: p.name, count: unassigned });
    }
    result.sort((a, b) => b.count - a.count);
    return result;
  }, [projects]);

  const totalUnassigned = unassignedByProject.reduce((s, p) => s + p.count, 0);

  const filteredWorkload = useMemo(() =>
    roleFilter ? teamWorkload.filter(m => m.role === roleFilter) : teamWorkload
  , [teamWorkload, roleFilter]);

  return (
    <div className="space-y-4">
      {/* Role summary strip */}
      <div className="flex flex-wrap gap-3">
        {ROLE_TILES.map(rt => (
          <button key={rt.key}
            onClick={() => setRoleFilter(prev => prev === rt.key ? null : rt.key)}
            className={`bg-white rounded-xl border px-4 py-3 flex items-center gap-2 flex-1 min-w-[120px] cursor-pointer transition-all text-left ${
              roleFilter === rt.key ? "border-amber-400 shadow-sm ring-1 ring-amber-200" : "border-slate-200 hover:border-amber-300"
            }`}>
            <div className={`w-8 h-8 rounded-full ${rt.bgColor} flex items-center justify-center flex-shrink-0`}>
              <svg className={`w-4 h-4 ${rt.iconColor}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                {rt.icon}
              </svg>
            </div>
            <div>
              <p className={`text-lg font-bold ${rt.color}`}>{roleCounts[rt.key]}</p>
              <p className="text-xs text-slate-500">{rt.label}</p>
              {rt.key === "admin" && roleCounts.admin === 0 && (
                <p className="text-[10px] text-slate-400 leading-tight">Excludes account owner</p>
              )}
            </div>
          </button>
        ))}
      </div>

      {/* Team Workload */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-stone-800">Team Workload</h3>
          <select value={projectFilter} onChange={e => setProjectFilter(e.target.value)}
            className="text-xs border border-slate-200 rounded-lg px-2 py-1 bg-white text-slate-500 focus:outline-none focus:ring-1 focus:ring-amber-400">
            <option value="all">All Projects</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-3 mb-3 text-[10px] text-slate-400">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-green-500" />Complete</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-blue-500" />In Progress</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-red-400" />Blocked</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-amber-400" />Pending</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-slate-200" />Not Started</span>
        </div>
        {filteredWorkload.length === 0 ? (
          <p className="text-xs text-slate-400">No {roleFilter ? roleFilter + " " : ""}assigned tasks{projectFilter !== "all" ? " for this project" : ""}.{roleFilter && <button onClick={() => setRoleFilter(null)} className="text-amber-600 ml-1 font-medium">Clear filter</button>}</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {filteredWorkload.map((member, i) => (
              <button key={member.name}
                onClick={() => onNavigate("contact-detail", member.name)}
                className="py-3 flex items-center gap-4 rounded-lg -mx-2 px-2 w-full text-left cursor-pointer hover:bg-slate-50 transition-colors group">
                <div className={`w-9 h-9 rounded-full ${AVATAR_COLORS[i % AVATAR_COLORS.length]} flex items-center justify-center flex-shrink-0`}>
                  <span className="text-white text-xs font-bold">{getInitials(member.name)}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-stone-800">{member.name}</p>
                    <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${ROLE_BADGE_COLORS[member.role] || "bg-slate-100 text-slate-600"}`}>
                      {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                    </span>
                    <span className="text-[10px] font-semibold text-amber-500 bg-amber-50 px-1 rounded hidden group-hover:inline">→ Contact Detail</span>
                  </div>
                  <p className="text-xs text-slate-400">{member.projectCount} project{member.projectCount !== 1 ? "s" : ""}{member.company ? ` · ${member.company}` : ""}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-bold text-stone-700">{member.tasks}</p>
                  <p className="text-xs text-slate-400">active tasks</p>
                </div>
                <div className="w-24 flex-shrink-0">
                  <MiniStatusBar statuses={member.statuses} total={member.tasks} />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Unassigned tasks warning */}
      {totalUnassigned > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-amber-500">⚠</span>
            <h3 className="text-sm font-semibold text-amber-800">{totalUnassigned} unassigned task{totalUnassigned !== 1 ? "s" : ""}</h3>
          </div>
          <div className="space-y-1.5">
            {unassignedByProject.map(up => (
              <button key={up.id}
                onClick={() => onNavigate("unassigned-tasks", up.id)}
                className="flex items-center justify-between text-xs rounded px-1 -mx-1 py-0.5 w-full text-left cursor-pointer hover:bg-amber-100/50 transition-colors group">
                <span className="text-amber-700">{up.name}</span>
                <span className="text-[10px] font-semibold text-amber-500 bg-amber-50 px-1 rounded hidden group-hover:inline">→ Tasks (unassigned)</span>
                <span className="text-amber-600 font-medium">{up.count} unassigned</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MiniStatusBar({ statuses, total }) {
  if (total === 0) return null;
  const segments = [
    { key: "Complete", color: "bg-green-500" },
    { key: "In Progress", color: "bg-blue-500" },
    { key: "Blocked", color: "bg-red-400" },
    { key: "Pending Approval", color: "bg-amber-400" },
    { key: "Not Started", color: "bg-slate-200" },
  ];
  return (
    <div className="flex gap-0.5">
      {segments.map((seg, i) => {
        const count = statuses[seg.key] || 0;
        if (count === 0) return null;
        const pct = Math.max(5, (count / total) * 100);
        const isFirst = !segments.slice(0, i).some(s => statuses[s.key]);
        const isLast = !segments.slice(i + 1).some(s => statuses[s.key]);
        return (
          <div key={seg.key}
            className={`h-4 ${seg.color} ${isFirst ? "rounded-l" : ""} ${isLast ? "rounded-r" : ""}`}
            style={{ width: `${pct}%` }}
            title={`${count} ${seg.key.toLowerCase()}`} />
        );
      })}
    </div>
  );
}
